import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { getTenantCurrent } from "@/services/authService";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CertificateGuideContent } from "@/pages/onboarding/certificate-guide-content";
import { DgiConfirmationPendingAlert } from "@/pages/onboarding/DgiConfirmationPendingAlert";
import {
  getStepHeadlineKey,
  getStepSubtitleKey,
} from "@/pages/onboarding/getOnboardingStepLabels";
import { OnboardingFooter } from "@/pages/onboarding/OnboardingFooter";
import { OnboardingSupportReplyCard } from "@/pages/onboarding/OnboardingSupportReplyCard";
import { OnboardingStepContent } from "@/pages/onboarding/OnboardingStepContent";
import { NoTenantWelcomeStep } from "@/pages/onboarding/NoTenantWelcomeStep";
import { VerifyCompanyStep } from "@/pages/onboarding/VerifyCompanyStep";
import { LegalDataStep } from "@/pages/onboarding/LegalDataStep";
import {
  OnboardingStep,
  OnboardingStepTracking,
} from "@/pages/onboarding/OnboardingStep";
import { useOnboarding } from "@/pages/onboarding/useOnboarding";
import type { OnboardingContext } from "@/pages/onboarding/useOnboarding";
import {
  clearPendingPartnerCode,
  getPendingPartnerCode,
} from "@/lib/pendingPartnerCode";

/**
 * Steps that own their full OnboardingShell (form + brand columns + internal
 * actions row). These bypass the wizard's PageHeader/Footer chrome and the
 * max-width wrapper so they can render edge-to-edge.
 *
 * Step 4 is shell-adopted only for the standalone path (non-accountant).
 * Delegated owners no longer have a step-4 screen — they finish at the plan
 * gate and continue from the dashboard handoff card.
 */
function isShellAdoptedStep(
  viewStep: number,
  ctx: {
    isAccountantFlowOwner: boolean;
    isAccountantUser: boolean;
    certificateAcquisitionChoice: "myself" | "fiscalo" | null;
    /** True when the user is on the Abitab "Presentate" sub-state of
     *  viewStep 6 fiscalo — the cert request exists with status
     *  in_progress and idDigitalRequestNumber is set. That branch
     *  renders the editorial OnboardingAbitabPresentStep. */
    fiscaloAbitabPresentActive: boolean;
    /** True when the user has acknowledged the Abitab visit and is waiting on
     *  the certificate (step6AbitabAcknowledgedAt SET). Renders the editorial
     *  OnboardingAwaitingCertificateStep. Sibling of the flag above and keyed
     *  on the same column inverted, because acknowledging used to drop the
     *  user out of the shell onto legacy chrome mid-flow. */
    fiscaloAwaitingCertificateActive: boolean;
    /** Resolved view step for the "Importar CFE anteriores" step, or null
     *  when the user doesn't need it (non-issuer / non-migrating). */
    cfeImportViewStep: number | null;
    /** Resolved view step for the plan-selection step (variant=single
     *  shell). For the self/accountantUser flow this is either
     *  `migrationOrPlanFirst` or `planMigrating` depending on
     *  isMigrating; for accountant-flow-owner it's viewStep 7. */
    planViewStep: number | null;
    /** Pre-DGI plan gate — owner-self user has finished situation but has
     *  no planId. Renders PlanStep in OnboardingShell, overrides whatever
     *  viewStep would normally show. */
    needsPlanGate: boolean;
    /** Owner-self final ready/success step (viewStep === totalSteps).
     *  Uses the editorial OnboardingShell variant=single, with its own
     *  "Ir al panel" button — no page-level chrome. */
    readyViewStep: number | null;
    /** Pre-DGI step insertion for the self path (2 = régimen + plan precede
     *  situation), 0 otherwise. The per-step checks below are keyed to the
     *  pre-régimen numbering, so the self path is normalized back by this. */
    preCertOffset: number;
  },
): boolean {
  const isSelf = !ctx.isAccountantFlowOwner && !ctx.isAccountantUser;
  // Resolved-integer steps (already offset-correct) — compare on the raw viewStep.
  if (ctx.needsPlanGate) return true;
  if (ctx.readyViewStep != null && viewStep === ctx.readyViewStep) return true;
  if (viewStep === 0 || viewStep === 1) return true;
  // Régimen: the self / accountant-flow-owner pre-plan step at raw viewStep 2
  // (plan is covered by needsPlanGate above). RegimeStep owns its OnboardingShell.
  if (viewStep === 2 && (isSelf || ctx.isAccountantFlowOwner)) return true;
  if (
    ctx.cfeImportViewStep != null &&
    viewStep === ctx.cfeImportViewStep &&
    isSelf
  ) {
    return true;
  }
  if (ctx.planViewStep != null && viewStep === ctx.planViewStep) return true;
  // Situation-and-later steps are tuned to the pre-régimen numbering; normalize
  // the self path back by the offset (accountant families use offset 0).
  const vs = isSelf ? viewStep - ctx.preCertOffset : viewStep;
  if (vs === 2) return true; // situation
  if (vs === 3) return true; // dgi access
  if (vs === 4 && !ctx.isAccountantFlowOwner && !ctx.isAccountantUser) {
    return true;
  }
  // Step 5: CertificateAcquisitionStep mode="content". Both manual
  // ("myself") and automatic ("fiscalo") paths now render inside the
  // editorial OnboardingShell with their own inline Volver/Siguiente.
  if (
    vs === 5 &&
    !ctx.isAccountantFlowOwner &&
    !ctx.isAccountantUser &&
    (ctx.certificateAcquisitionChoice === "myself" ||
      ctx.certificateAcquisitionChoice === "fiscalo")
  ) {
    return true;
  }
  // Step 6: CertificateStepContent renders for accountant users and for owners
  // who chose the manual cert path. The Fiscalo-cert "presentate en Abitab"
  // view is also editorial now (OnboardingAbitabPresentStep). Other Fiscalo
  // variants (pending / failed / awaiting-acknowledgment) keep the legacy
  // chrome for now.
  if (vs === 6) {
    if (ctx.isAccountantUser) return true;
    if (!ctx.isAccountantFlowOwner && ctx.certificateAcquisitionChoice !== "fiscalo") {
      return true;
    }
    if (
      !ctx.isAccountantFlowOwner &&
      ctx.certificateAcquisitionChoice === "fiscalo" &&
      (ctx.fiscaloAbitabPresentActive || ctx.fiscaloAwaitingCertificateActive)
    ) {
      return true;
    }
  }
  // CAE step (owner-self path only — accountants don't see CAE here).
  // Owns its own OnboardingShell, so skip the legacy PageHeader/Footer.
  if (vs === 7 && !ctx.isAccountantFlowOwner && !ctx.isAccountantUser) {
    return true;
  }
  // CFE-import step (resolved at runtime, depends on whether the user
  // needs it). Uses OnboardingShell with its own brand column + Skip/
  // Continue actions, so it must opt out of the page-level PageHeader
  // + OnboardingFooter chrome.
  if (
    ctx.cfeImportViewStep != null &&
    viewStep === ctx.cfeImportViewStep &&
    !ctx.isAccountantFlowOwner &&
    !ctx.isAccountantUser
  ) {
    return true;
  }
  // Plan-selection step — same treatment, has its own OnboardingShell
  // variant=single + inline Volver/Siguiente bar.
  if (ctx.planViewStep != null && viewStep === ctx.planViewStep) {
    return true;
  }
  return false;
}

function OnboardingPageBody({
  onboarding,
}: {
  onboarding: OnboardingContext;
}): JSX.Element {
  const { t } = useTranslation();
  const [certificateGuideOpen, setCertificateGuideOpen] = useState(false);

  const fiscaloAbitabPresentActive =
    onboarding.viewStep === onboarding.abitabStep &&
    onboarding.certificateAcquisitionChoice === "fiscalo" &&
    Boolean(onboarding.currentCertificateRequest) &&
    !onboarding.tenant?.step6AbitabAcknowledgedAt;
  // Sibling of fiscaloAbitabPresentActive, keyed on the same column inverted:
  // once the Abitab visit is acknowledged the wait screen takes over, and it is
  // editorial too.
  const fiscaloAwaitingCertificateActive =
    onboarding.certificateAcquisitionChoice === "fiscalo" &&
    Boolean(onboarding.tenant?.step6AbitabAcknowledgedAt);
  const shellAdopted = isShellAdoptedStep(onboarding.viewStep, {
    isAccountantFlowOwner: onboarding.isAccountantFlowOwner,
    isAccountantUser: onboarding.isAccountantUser,
    certificateAcquisitionChoice: onboarding.certificateAcquisitionChoice,
    fiscaloAbitabPresentActive,
    fiscaloAwaitingCertificateActive,
    preCertOffset: onboarding.preCertOffset,
    cfeImportViewStep: onboarding.needsCfeImportStep
      ? (onboarding.ownerPostCertViewSteps.cfe ?? null)
      : null,
    planViewStep: onboarding.isAccountantFlowOwner
      ? 7
      : !onboarding.isAccountantUser
        ? (onboarding.isMigrating
            ? onboarding.ownerPostCertViewSteps.planMigrating
            : onboarding.ownerPostCertViewSteps.migrationOrPlanFirst)
        : null,
    needsPlanGate: onboarding.needsPlanGate,
    readyViewStep: onboarding.isAccountantFlowOwner ? null : onboarding.totalSteps,
  });

  const isAlreadyIssuer =
    onboarding.situationChoice === "already_issuer" ||
    onboarding.tenant?.onboardingSituationChoice === "already_issuer";
  const showCertGuideIcon =
    ((!onboarding.isAccountantFlowOwner && !onboarding.isAccountantUser) &&
      onboarding.viewStep === onboarding.certContentStep) ||
    (isAlreadyIssuer &&
      onboarding.viewStep === onboarding.abitabStep &&
      !onboarding.isAccountantFlowOwner &&
      !onboarding.isAccountantUser);
  useEffect(() => {
    if (!showCertGuideIcon) setCertificateGuideOpen(false);
  }, [showCertGuideIcon]);

  if (
    onboarding.loading ||
    !onboarding.tenant ||
    !onboarding.viewStepSynced
  ) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (onboarding.tenant.onboardingCompletedAt) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  const step6FiscaloGoToAbitab =
    onboarding.viewStep === onboarding.abitabStep &&
    onboarding.certificateAcquisitionChoice === "fiscalo" &&
    onboarding.currentCertificateRequest?.status === "in_progress" &&
    Boolean(onboarding.currentCertificateRequest?.idDigitalRequestNumber);
  const step6AwaitingAbitab =
    onboarding.viewStep === onboarding.abitabStep &&
    onboarding.certificateAcquisitionChoice === "fiscalo" &&
    Boolean(onboarding.tenant?.step6AbitabAcknowledgedAt);
  const headlineKey = getStepHeadlineKey(
    onboarding.viewStep,
    onboarding.totalSteps,
    onboarding.isMigrating,
    onboarding.isAccountantFlowOwner,
    onboarding.isAccountantUser,
    isAlreadyIssuer,
    onboarding.certificateAcquisitionChoice,
    step6FiscaloGoToAbitab,
    step6AwaitingAbitab,
    onboarding.isAccountantFlowOwner || onboarding.isAccountantUser
      ? null
      : onboarding.ownerPostCertViewSteps,
    onboarding.needsCfeImportStep,
    onboarding.preCertOffset
  );
  const subtitleKey = getStepSubtitleKey(
    onboarding.viewStep,
    onboarding.totalSteps,
    onboarding.isMigrating,
    onboarding.isAccountantFlowOwner,
    onboarding.isAccountantUser,
    isAlreadyIssuer,
    onboarding.certificateAcquisitionChoice,
    step6FiscaloGoToAbitab,
    step6AwaitingAbitab,
    onboarding.isAccountantFlowOwner || onboarding.isAccountantUser
      ? null
      : onboarding.ownerPostCertViewSteps,
    onboarding.needsCfeImportStep,
    onboarding.preCertOffset
  );

  const earlyHeadlineStep =
    onboarding.viewStep === 0 ||
    onboarding.viewStep === 1 ||
    onboarding.viewStep === onboarding.situationStep;
  const headlineLegalName = earlyHeadlineStep
    ? (onboarding.tenant?.legalName?.trim() ||
        onboarding.tenant?.name?.trim() ||
        (t("onboarding.introHeadlineFallbackCompany") as string))
    : null;
  const titleText =
    headlineLegalName != null && earlyHeadlineStep
      ? t(headlineKey, { legalName: headlineLegalName })
      : t(headlineKey);

  const title = showCertGuideIcon ? (
    <span className="inline-flex items-center gap-2">
      {titleText}
      <button
        type="button"
        onClick={() => setCertificateGuideOpen(true)}
        className="inline-flex shrink-0 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t("onboarding.certificateGuide.title")}
      >
        <Info className="h-5 w-5" />
      </button>
    </span>
  ) : (
    titleText
  );

  const tenantDgiConfirmed =
    Boolean(onboarding.tenant?.dgiPortalUserConfirmed) ||
    Boolean(onboarding.tenant?.dgiPortalUserConfirmedAt);
  const showDgiConfirmationAlert = Boolean(
    onboarding.dgiConfirmationStatusResolved &&
      onboarding.viewStep === onboarding.dgiStep &&
      onboarding.tenant?.hasDgiPortalUser &&
      !tenantDgiConfirmed &&
      (onboarding.dgiStepsCompletedConfirmed ?? false)
  );

  return (
    <>
      {!shellAdopted && (
        <>
          <PageHeader title={title} description={t(subtitleKey)} />
          <DgiConfirmationPendingAlert visible={showDgiConfirmationAlert} />
        </>
      )}
      <OnboardingStepContent onboarding={onboarding} />
      {!shellAdopted && (
        <OnboardingFooter
          viewStep={onboarding.viewStep}
          totalSteps={onboarding.totalSteps}
          isLastStep={onboarding.isLastStep}
          isAccountantFlowOwner={onboarding.isAccountantFlowOwner}
          isAccountantUser={onboarding.isAccountantUser}
          situationChoice={onboarding.situationChoice}
          dgiPasswordReady={onboarding.dgiPasswordReady}
          dgiStepsCompletedConfirmed={onboarding.dgiStepsCompletedConfirmed ?? false}
          canFinish={onboarding.canFinish}
          finishing={onboarding.finishing}
          nextDisabled={onboarding.nextButtonDisabled}
          nextLoading={onboarding.nextButtonLoading}
          onBack={onboarding.handleBackStep}
          onNext={onboarding.handleNextStep}
          onFinish={onboarding.handleFinish}
        />
      )}
      {showCertGuideIcon && (
        <Dialog open={certificateGuideOpen} onOpenChange={setCertificateGuideOpen}>
          <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
            <DialogHeader>
              <DialogTitle>
                {t("onboarding.certificateGuide.title")}
              </DialogTitle>
            </DialogHeader>
            <div className="-mr-2 overflow-y-auto pr-2">
              <CertificateGuideContent />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCertificateGuideOpen(false)}
                className="transition-colors duration-150"
              >
                {t("onboarding.certificateGuide.backToOnboarding")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <OnboardingSupportReplyCard />
    </>
  );
}

function NoTenantOnboardingView(): JSX.Element {
  const { createCompanyAndSetUser, refreshUser, user } = useAuth();
  const [showVerifyCompany, setShowVerifyCompany] = useState(false);

  const handleCreateCompany = async (
    rut: string,
    companyData?: Parameters<typeof createCompanyAndSetUser>[1]
  ): Promise<void> => {
    // Google signups (and email/password signups without a RUC yet) had
    // no Company to attach a partner redemption to at registration time —
    // this is the first point one exists. See pendingPartnerCode.ts.
    const partnerCode = getPendingPartnerCode();
    await createCompanyAndSetUser(rut, {
      ...companyData,
      ...(partnerCode ? { partnerCode } : {}),
    });
    clearPendingPartnerCode();
    // No signup reporting here any more. This used to be the fire point for
    // everyone without a RUC at registration (Google SSO, deferred RUC), which
    // made it a proxy for "signup" that a user abandoning onboarding never
    // reached. The backend reports it when the user row is created, so an
    // abandoned onboarding still counts as the signup it was.
    await refreshUser();
  };

  // Paso 01 + Paso 02 both render their own full-bleed OnboardingShell —
  // no outer wrapper needed here.
  if (!showVerifyCompany) {
    return (
      <OnboardingStep name="no_tenant">
        <NoTenantWelcomeStep
          onExistingCompanySelected={() => setShowVerifyCompany(true)}
        />
      </OnboardingStep>
    );
  }

  return (
    <OnboardingStep name="verify_company">
      <VerifyCompanyStep
        onCreateCompany={handleCreateCompany}
        onBack={() => setShowVerifyCompany(false)}
      />
    </OnboardingStep>
  );
}

function OnboardingPageContent(): JSX.Element {
  const { user, refreshUser } = useAuth();
  const onboarding = useOnboarding();

  const { t } = useTranslation();
  const { data: tenant } = useQuery({
    queryKey: ["tenant", "current", user?.tenantId],
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId != null && !user?.identityVerifiedAt),
  });

  if (user && user.tenantId == null) {
    return <NoTenantOnboardingView />;
  }

  if (user && user.tenantId != null && !user.identityVerifiedAt && !user.documentNumber) {
    return (
      <OnboardingStep name="legal_data" onboarding={onboarding}>
        <LegalDataStep
          companyType={tenant?.companyType ?? null}
          legalName={tenant?.legalName ?? tenant?.name ?? null}
          initialValues={{
            firstName: user.firstName ?? null,
            secondName: user.secondName ?? null,
            firstLastName: user.lastName ?? null,
            secondLastName: user.secondLastName ?? null,
            documentType: user.documentType ?? null,
            documentNumber: user.documentNumber ?? null,
          }}
          onSuccess={() => void refreshUser()}
        />
      </OnboardingStep>
    );
  }

  if (
    isShellAdoptedStep(onboarding.viewStep, {
      isAccountantFlowOwner: onboarding.isAccountantFlowOwner,
      isAccountantUser: onboarding.isAccountantUser,
      certificateAcquisitionChoice: onboarding.certificateAcquisitionChoice,
      fiscaloAbitabPresentActive:
        onboarding.viewStep === onboarding.abitabStep &&
        onboarding.certificateAcquisitionChoice === "fiscalo" &&
        Boolean(onboarding.currentCertificateRequest) &&
        !onboarding.tenant?.step6AbitabAcknowledgedAt,
      fiscaloAwaitingCertificateActive:
        onboarding.viewStep === onboarding.abitabStep &&
        onboarding.certificateAcquisitionChoice === "fiscalo" &&
        Boolean(onboarding.tenant?.step6AbitabAcknowledgedAt),
      preCertOffset: onboarding.preCertOffset,
      cfeImportViewStep: onboarding.needsCfeImportStep
        ? (onboarding.ownerPostCertViewSteps.cfe ?? null)
        : null,
      planViewStep: onboarding.isAccountantFlowOwner
        ? 7
        : !onboarding.isAccountantUser
          ? (onboarding.isMigrating
              ? onboarding.ownerPostCertViewSteps.planMigrating
              : onboarding.ownerPostCertViewSteps.migrationOrPlanFirst)
          : null,
      needsPlanGate: onboarding.needsPlanGate,
      readyViewStep: onboarding.isAccountantFlowOwner ? null : onboarding.totalSteps,
    })
  ) {
    return <OnboardingPageBody onboarding={onboarding} />;
  }

  return (
    <div className="mx-auto flex min-h-0 max-w-4xl flex-col space-y-4 p-6">
      <OnboardingPageBody onboarding={onboarding} />
    </div>
  );
}

export default OnboardingPage;

/**
 * Thin wrapper so step tracking spans the WHOLE visit, including the early
 * returns (no_tenant, verify_company, legal_data) that render before the
 * wizard — those are steps too, and the earliest drop-off points there are.
 *
 * Unmounting this provider IS leaving onboarding, which is what completes the
 * final step.
 */
export function OnboardingPage(): JSX.Element {
  return (
    <OnboardingStepTracking>
      <OnboardingPageContent />
    </OnboardingStepTracking>
  );
}
